import { Check, X } from "lucide-react";
import { useContent } from "@/lib/ContentContext";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { ClosingCTA } from "@/pages/Home";
import { SpendCalculator } from "@/components/mocks/ProductMocks";

export default function WhyResponsiq() {
  const { differentiators, comparison } = useContent();
  return (
    <>
      <section className="border-b border-[#D8E2E6]" data-testid="why-hero">
        <div className="container-riq pt-16 pb-20 lg:pt-24 lg:pb-24">
          <p className="eyebrow mb-5">Why RresponsIQ</p>
          <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
            <MaskedLine delay={0.1}>Break up with</MaskedLine>
            <MaskedLine delay={0.22}>
              the <span className="accent-word">black box.</span>
            </MaskedLine>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#4A5A63] leading-relaxed max-w-2xl">
            Legacy DSPs bundle media, tech and data into one opaque CPM and call it performance. RresponsIQ itemizes everything — and was built by the people who used to run the other side of the auction.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="differentiators">
        <div className="container-riq">
          <Reveal>
            <p className="eyebrow mb-3">The manifesto</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
              Five commitments, in <span className="accent-word">writing.</span>
            </h2>
          </Reveal>
          <div className="mt-12 border-y border-[#D8E2E6] divide-y divide-[#D8E2E6]">
            {differentiators.map((d, i) => (
              <Reveal key={d.id} delay={i * 0.05}>
                <article className="grid md:grid-cols-12 gap-4 py-8 items-baseline group" data-testid={`manifesto-${d.id}`}>
                  <span className="md:col-span-2 font-display font-black text-5xl lg:text-6xl text-[#0D0D0F]/10 group-hover:text-[#0E7A8F] transition-colors duration-300">{d.num}</span>
                  <h3 className="md:col-span-4 font-display font-black text-xl sm:text-2xl tracking-tight">{d.title}</h3>
                  <p className="md:col-span-6 text-[#4A5A63] leading-relaxed">{d.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#EDF3F5] border-y border-[#D8E2E6]" data-testid="comparison-section">
        <div className="container-riq">
          <Reveal>
            <p className="eyebrow mb-3">Side by side</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
              RresponsIQ vs. the <span className="accent-word">black box.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 overflow-x-auto card-brut" data-testid="comparison-table">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="bg-[#0D0D0F] text-[#F7F9FA]">
                    <th scope="col" className="font-mono2 text-xs uppercase tracking-widest font-bold px-5 py-4">Capability</th>
                    <th scope="col" className="font-mono2 text-xs uppercase tracking-widest font-bold px-5 py-4 text-[#0E7A8F]">RresponsIQ</th>
                    <th scope="col" className="font-mono2 text-xs uppercase tracking-widest font-bold px-5 py-4 text-[#F7F9FA]/50">Black-Box DSP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D8E2E6]">
                  {comparison.map((row, i) => (
                    <tr key={i} className="hover:bg-[#E4F3F7]/50 transition-colors" data-testid={`comparison-row-${i}`}>
                      <th scope="row" className="px-5 py-4 font-display font-bold text-sm">{row.capability}</th>
                      <td className="px-5 py-4 text-sm">
                        <span className="flex items-start gap-2"><Check className="w-4 h-4 text-[#0E7A8F] mt-0.5 shrink-0" aria-hidden="true" />{row.responsiq}</span>
                      </td>
                      <td className="px-5 py-4 text-sm text-[#4A5A63]">
                        <span className="flex items-start gap-2"><X className="w-4 h-4 text-[#4A5A63]/50 mt-0.5 shrink-0" aria-hidden="true" />{row.blackbox}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
          <div className="mt-14">
            <SpendCalculator />
          </div>
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}
